import { forwardRef, type ChangeEvent, type KeyboardEvent } from 'react';
import { parseNutrientValue } from '../../../utils';
import styles from './NutrientInput.module.css';

interface NutrientInputProps {
	value: number;
	onChange: (value: number) => void;
	onBlur?: () => void;
	onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
	disabled?: boolean;
	error?: boolean;
	min?: number;
	max?: number;
	className?: string;
	'data-row'?: number;
	'data-col'?: number;
}

export const NutrientInput = forwardRef<HTMLInputElement, NutrientInputProps>(
	(
		{
			value,
			onChange,
			onBlur,
			onKeyDown,
			disabled = false,
			error = false,
			min = 0,
			max = 100,
			className = '',
			'data-row': dataRow,
			'data-col': dataCol,
		},
		ref
	) => {
		const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
			const rawValue = e.target.value;

			if (rawValue === '') {
				onChange(Number.NaN);
				return;
			}

			const numValue = parseNutrientValue(rawValue);

			if (numValue >= min && numValue <= max) {
				onChange(numValue);
			}
		};

		const handleBlur = () => {
			if (onBlur) {
				onBlur();
			}
		};

		const displayValue = Number.isNaN(value) ? '' : value;

		return (
			<input
				ref={ref}
				type="number"
				value={displayValue}
				onChange={handleChange}
				onBlur={handleBlur}
				onKeyDown={onKeyDown}
				disabled={disabled}
				min={min}
				max={max}
				step="0.01"
				placeholder="---"
				className={`${styles.input} ${error ? styles.error : ''} ${disabled ? styles.disabled : ''} ${className}`}
				aria-invalid={error}
				data-row={dataRow}
				data-col={dataCol}
			/>
		);
	}
);

NutrientInput.displayName = 'NutrientInput';
